import React, { useState } from "react";
import { Send, Link2, MailCheck, MessageSquareReply } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const CHOICE = { repair: ["Repareren", "text-rose-600"], check: ["Controleren", "text-amber-600"], later: ["Later", "text-muted-foreground"] };

/* "Rapport naar klant" — stuurt controlerapport per e-mail + toont klantreactie. Gebruikt op monteurkaart en werkbon. */
export const SendReportButton = ({ card, t, locked, onSent, compact }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(card.customer_email || "");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const rep = card.inspection_report || {};
  const items = Object.entries(card.inspection?.items || {}).filter(([k]) => !k.startsWith("svc_"));
  const resp = rep.response;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/repairs/${card.id}/inspection-report/send`, { email: email || null, message });
      toast.success(`${t("reportSent")} → ${data.sent_to}`);
      setOpen(false); onSent?.();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const copyLink = async () => {
    try { const { data } = await api.get(`/repairs/${card.id}/inspection-report/link`); await navigator.clipboard.writeText(data.url); toast.success(t("linkCopied")); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-2" data-testid="send-report-block">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size={compact ? "sm" : "default"} disabled={locked || items.length === 0} onClick={() => setOpen(true)} className="rounded-full bg-primary" data-testid="send-report-btn"><Send className="h-4 w-4 mr-1.5" />{t("sendReportToCustomer")}</Button>
        <Button size={compact ? "sm" : "default"} variant="outline" onClick={copyLink} disabled={items.length === 0} className="rounded-full" data-testid="copy-report-link"><Link2 className="h-4 w-4 mr-1.5" />{t("copyLink")}</Button>
        {rep.sent_at && <span className="text-xs text-muted-foreground flex items-center gap-1" data-testid="report-sent-info"><MailCheck className="h-3.5 w-3.5 text-emerald-600" />{t("reportSentAt")} {new Date(rep.sent_at).toLocaleString()} · {rep.sent_to}</span>}
      </div>
      {resp && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm space-y-1" data-testid="report-customer-response">
          <div className="font-semibold flex items-center gap-1.5"><MessageSquareReply className="h-4 w-4 text-emerald-600" />{t("customerResponded")} · {new Date(resp.at).toLocaleString()}</div>
          {(resp.lines || []).map((l) => <div key={l.key} className="flex justify-between gap-2"><span>{l.label}</span><b className={CHOICE[l.choice]?.[1]}>{CHOICE[l.choice]?.[0]}</b></div>)}
          {(resp.preferred_date || resp.phone) && <div className="text-xs text-muted-foreground">{resp.preferred_date && `${t("preferredDate")}: ${resp.preferred_date} ${({ morning: "voormiddag", afternoon: "namiddag" })[resp.preferred_time] || ""}`} {resp.phone && ` · ${resp.phone}`}</div>}
          {resp.message && <div className="text-xs italic">“{resp.message}”</div>}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t("sendReportToCustomer")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{items.length} {t("checkpoints")} · {items.filter(([, v]) => v.status !== "ok").length} {t("mech_flagged")}</div>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("customerEmailPh")} data-testid="send-report-email" />
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder={t("reportMessagePh")} className="w-full rounded-md border border-input bg-background p-2 text-sm" data-testid="send-report-message" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={send} disabled={busy} className="rounded-full" data-testid="send-report-confirm"><Send className="h-4 w-4 mr-1.5" />{t("send")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
